import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Size comparison rows that mirror the variant picker: clicking a row selects that variant
 * through Horizon's own picker, and a variant change (from the pills) highlights the row.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement[]} rows
 *
 * @extends {Component<Refs>}
 */
class DtSizeVizComponent extends Component {
  requiredRefs = ['rows'];
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.closest('.shopify-section, dialog')?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, {
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /** on:click="/select/{{ index }}" */
  select(index) {
    const row = this.refs.rows[Number(index)];
    const input = this.closest('.shopify-section')?.querySelector(
      `variant-picker input[type="radio"][data-variant-id="${row?.dataset.variantId}"]`
    );
    if (!(input instanceof HTMLInputElement)) return;
    // No `input.checked` early return: if the rows and the picker ever disagree, that guard would
    // make the disagreement permanent. Re-highlight from the row, then let the picker do its work.
    if (input.checked) return this.#highlight(String(row.dataset.variantId));
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  #onProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    // detail.variantId is set synchronously; the promise can resolve variant: null for unavailable combos.
    if (event.detail?.variantId) return this.#highlight(String(event.detail.variantId));
    event.promise
      ?.then(({ variant }) => variant && this.#highlight(String(variant.id).split('/').pop()))
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[dt-size-viz] Event promise rejected:', error);
      });
  };

  /** @param {string} id */
  #highlight(id) {
    this.refs.rows.forEach((row) => {
      const active = row.dataset.variantId === id;
      row.classList.toggle('is-active', active);
      row.setAttribute('aria-pressed', String(active));
    });
  }
}

if (!customElements.get('dt-size-viz-component')) {
  customElements.define('dt-size-viz-component', DtSizeVizComponent);
}
